import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Database, Download, Play, ShieldCheck, CloudDownload, AlertTriangle, History, HardDrive, RefreshCw, ArrowLeftRight, FileDown, FileUp } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { toast } from "sonner";
import { getMeta, setMeta, META } from "@/lib/db";
import { downloadLocalBackupNow, getLastLocalExportAt } from "@/lib/localBackup";
import { repairSync } from "@/lib/sync";
import { useOnline } from "@/lib/useOnline";
import { OfflineNotice } from "@/components/OfflineNotice";

const KIND_LABEL = {
  daily: { label: "Diario automático", variant: "outline" },
  weekly: { label: "Semanal automático", variant: "outline" },
  pre_op: { label: "Antes de una operación", variant: "secondary" },
  manual: { label: "Manual", variant: "secondary" },
};

export default function BackupsSettings() {
  const navigate = useNavigate();
  const online = useOnline();
  const [backups, setBackups] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [downloadingNow, setDownloadingNow] = useState(false);
  const [token, setToken] = useState("");
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [persisted, setPersisted] = useState(null);
  const [retryingPersist, setRetryingPersist] = useState(false);
  const [lastLocalExport, setLastLocalExport] = useState(null);
  const [downloadingLocal, setDownloadingLocal] = useState(false);

  // Migración de cuenta: exportar todo / importar archivo de otra cuenta.
  const importFileRef = useRef(null);
  const [exportingAll, setExportingAll] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importMode, setImportMode] = useState("replace");
  const [importing, setImporting] = useState(false);

  useEffect(() => { getMeta(META.AUTH_TOKEN).then((t)=>setToken(t || "")); }, []);

  // Tanda 3 — Resguardo local reforzado: estado real del almacenamiento
  // persistente (consultado directo al navegador, no solo lo guardado en
  // META) + fecha de la última exportación local guardada en el dispositivo.
  useEffect(() => {
    (async () => {
      if (navigator.storage?.persisted) {
        setPersisted(await navigator.storage.persisted());
      } else {
        setPersisted(await getMeta(META.STORAGE_PERSISTED));
      }
      setLastLocalExport(await getLastLocalExportAt());
    })();
  }, []);

  const retryPersist = async () => {
    setRetryingPersist(true);
    try {
      const granted = navigator.storage?.persist ? await navigator.storage.persist() : false;
      await setMeta(META.STORAGE_PERSISTED, granted);
      setPersisted(granted);
      toast[granted ? "success" : "error"](granted
        ? "Almacenamiento persistente activado — tus datos ahora resisten mejor la falta de espacio del dispositivo."
        : "El navegador no otorgó almacenamiento persistente. Igual, tus datos se resguardan en la nube automáticamente.");
    } finally { setRetryingPersist(false); }
  };

  const downloadLocalNow = async () => {
    setDownloadingLocal(true);
    try {
      const counts = await downloadLocalBackupNow();
      const total = Object.values(counts).reduce((a, v) => a + v, 0);
      setLastLocalExport(new Date().toISOString());
      toast.success(`Copia local guardada en tu dispositivo (${total} registros). Funciona sin conexión.`);
    } catch (e) {
      toast.error("No pude generar la copia local");
    } finally { setDownloadingLocal(false); }
  };

  const exportAllData = async () => {
    setExportingAll(true);
    try {
      const res = await fetch(api.migrationExportUrl(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("No pude generar la exportación");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("content-disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `cuentavaca-export-${new Date().toISOString().slice(0,10)}.json.gz`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportación descargada. Guardá este archivo para importarlo en la cuenta nueva.");
    } catch (e) {
      toast.error(e?.message || "No pude generar la exportación");
    } finally { setExportingAll(false); }
  };

  const onImportFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportFile(file);
    setImporting(true);
    try {
      const preview = await api.migrationImportPreview(file);
      setImportPreview(preview);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pude leer el archivo. Debe ser un .json.gz (o .json) exportado desde Cuenta Vaca.");
      setImportFile(null);
    } finally { setImporting(false); }
  };

  const cancelImport = () => { setImportPreview(null); setImportFile(null); };

  const confirmImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const r = await api.migrationImport(importFile, { mode: importMode, confirm: true });
      setImportPreview(null);
      setImportFile(null);
      // Los registros importados conservan su updated_at original (de la
      // cuenta de origen), casi siempre más viejo que el cursor de
      // sincronización que este dispositivo ya tiene — sin esto, el pull
      // incremental normal los excluye para siempre. repairSync() resetea
      // el cursor y fuerza un pull completo, para que lo importado aparezca
      // ya mismo en Animales/Dashboard/Fincas.
      await repairSync();
      toast.success(r.message?.trim() || `Importación completa — ${r.total} registros. Ya están disponibles en la app.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo importar el archivo");
    } finally { setImporting(false); }
  };

  const load = () => {
    if (!navigator.onLine) { setLoading(false); return; }
    setLoading(true);
    Promise.all([api.listBackups(), api.backupHealth()])
      .then(([b, h]) => { setBackups(b); setHealth(h); })
      .finally(()=>setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true);
    try { await api.runBackup(); toast.success("Backup creado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setRunning(false); }
  };

  const downloadNow = async () => {
    setDownloadingNow(true);
    try {
      const res = await fetch(api.backupDownloadNowUrl(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("No pude generar el backup");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cuenta-vaca-backup-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Copia descargada en tu dispositivo");
    } catch (e) {
      toast.error(e?.message || "No pude descargar la copia");
    } finally { setDownloadingNow(false); }
  };

  const download = async (id, kind) => {
    const res = await fetch(api.backupDownloadUrl(id), { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `backup-${kind}-${id}.json.gz`; a.click();
    URL.revokeObjectURL(url);
  };

  const confirmRestore = async () => {
    if (!restoreTarget) return;
    setRestoring(true);
    try {
      const r = await api.restoreBackup(restoreTarget.id);
      const total = Object.values(r.restored_counts || {}).reduce((a, v) => a + v, 0);
      toast.success(`Restaurado — ${total} registros. Se generó un backup del estado anterior por seguridad.`);
      setRestoreTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo restaurar el backup");
    } finally { setRestoring(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={()=>navigate("/ajustes")} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <h1 className="font-heading text-2xl font-semibold flex-1">Backups</h1>
      </div>

      {!online && <OfflineNotice label="Los backups en la nube y descargas del servidor requieren conexión." />}

      {/* Resguardo local (Tanda 3): funciona SIN conexión — vive solo en este dispositivo. */}
      <Card data-testid="local-backup-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius)] bg-accent text-accent-foreground shrink-0">
              <HardDrive size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-heading font-semibold text-[15px]">Resguardo local en este dispositivo</h2>
              <p className="text-[13px] text-muted-foreground mt-1">
                Una copia adicional guardada directamente en tu celular/tablet, sin depender de internet.
                Se genera sola una vez por semana, o cuando la pidas.
              </p>
              <div className="mt-2.5 flex items-center gap-2 flex-wrap text-[12.5px]">
                <span className="text-muted-foreground">Almacenamiento persistente:</span>
                {persisted === null ? (
                  <span className="text-muted-foreground">verificando…</span>
                ) : persisted ? (
                  <Badge className="bg-emerald-600/90 hover:bg-emerald-600 text-[10px]" data-testid="storage-persist-ok">Activado</Badge>
                ) : (
                  <>
                    <Badge variant="destructive" className="text-[10px]" data-testid="storage-persist-warn">No activado</Badge>
                    <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={retryPersist} disabled={retryingPersist} data-testid="storage-persist-retry">
                      <RefreshCw size={11} className="mr-1" />Reintentar
                    </Button>
                  </>
                )}
              </div>
              <p className="text-[11.5px] text-muted-foreground mt-1.5" data-testid="local-backup-last">
                Última copia local: {lastLocalExport ? formatDateTime(lastLocalExport) : "todavía no se generó ninguna"}
              </p>
            </div>
          </div>
          <Button onClick={downloadLocalNow} disabled={downloadingLocal} variant="outline" className="h-11 w-full" data-testid="local-backup-download-now">
            <HardDrive size={16} className="mr-2" />
            {downloadingLocal ? "Generando…" : "Descargar copia local ahora"}
          </Button>
        </CardContent>
      </Card>

      {online && health?.warn && (
        <Card className="border-destructive/50 bg-destructive/5" data-testid="backup-health-warning">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle size={20} className="text-destructive shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-sm">
                {health.hours_since_last_success == null
                  ? "Todavía no se generó ningún backup automático."
                  : `Hace más de ${Math.round(health.hours_since_last_success)} horas que no se genera un backup con éxito.`}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Tocá "Crear ahora" abajo para generar uno de inmediato.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resguardo principal: descarga directa */}
      <Card className="border-primary/30">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius)] bg-primary/10 text-primary shrink-0">
              <ShieldCheck size={22} />
            </span>
            <div className="min-w-0">
              <h2 className="font-heading font-semibold text-[15px]">Tu información está segura</h2>
              <p className="text-[13px] text-muted-foreground mt-1">
                Todo lo que cargás (fincas, lotes, animales, eventos e inventarios) se respalda
                automáticamente todos los días en la nube (guardado de forma permanente, separado
                de esta app) y queda disponible acá para descargar o restaurar. Igual, te
                recomendamos descargar una copia completa cada tanto para tenerla siempre a mano.
              </p>
              {online && health?.last_success && !health.warn && (
                <p className="text-[12px] text-emerald-700 dark:text-emerald-400 mt-1.5" data-testid="backup-health-ok">
                  Último backup automático exitoso: {formatDateTime(health.last_success.started_at)}
                </p>
              )}
            </div>
          </div>
          <Button onClick={downloadNow} disabled={downloadingNow || !online} className="h-12 w-full" data-testid="backups-download-now">
            <CloudDownload size={18} className="mr-2" />
            {downloadingNow ? "Generando copia…" : "Descargar copia completa ahora"}
          </Button>
        </CardContent>
      </Card>

      {/* Migración de cuenta: llevarse todos los datos a otra cuenta/instancia */}
      <Card data-testid="migration-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius)] bg-accent text-accent-foreground shrink-0">
              <ArrowLeftRight size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-heading font-semibold text-[15px]">Migrar cuenta</h2>
              <p className="text-[13px] text-muted-foreground mt-1">
                Exportá todos tus datos (fincas, lotes, animales, eventos, inventarios) a un
                archivo para llevarlos a otra cuenta o instancia, o importá un archivo generado
                antes.
              </p>
              <p className="text-[11.5px] text-muted-foreground mt-1">
                El archivo incluye también los animales en la papelera (los conteos pueden ser
                mayores a lo que ves en Animales).
              </p>
            </div>
          </div>
          <Button onClick={exportAllData} disabled={exportingAll || !online} variant="outline" className="h-11 w-full" data-testid="migration-export-btn">
            <FileDown size={16} className="mr-2" />
            {exportingAll ? "Generando…" : "Exportar todos mis datos"}
          </Button>
          <input
            ref={importFileRef}
            type="file"
            accept=".json,.gz,application/gzip,application/json"
            className="hidden"
            onChange={onImportFileSelected}
            data-testid="migration-import-file-input"
          />
          <Button onClick={()=>importFileRef.current?.click()} disabled={importing || !online} variant="outline" className="h-11 w-full" data-testid="migration-import-btn">
            <FileUp size={16} className="mr-2" />
            {importing && !importPreview ? "Leyendo archivo…" : "Importar datos"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Database size={16} /> Copias guardadas</CardTitle>
          <Button onClick={run} disabled={running || !online} variant="outline" size="sm" data-testid="backups-run"><Play size={14} className="mr-1"/>{running ? "Creando…" : "Crear ahora"}</Button>
        </CardHeader>
        <CardContent className="p-0 divide-y divide-border">
          {online && !loading && backups.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Aún no hay copias guardadas en el servidor. Usá el botón de arriba para descargar una al instante.</p>}
          {backups.map((b) => {
            const kindInfo = KIND_LABEL[b.kind] || { label: b.kind, variant: "outline" };
            return (
              <div key={b.id} className="p-3 flex items-center gap-2" data-testid={`backup-row-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                    {formatDateTime(b.snapshot_date)}
                    <Badge variant={kindInfo.variant} className="text-[10px]">{kindInfo.label}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">{Math.round(b.size_bytes / 1024)} KB · {Object.values(b.entities_count || {}).reduce((a, v) => a + v, 0)} registros{b.label ? ` · ${b.label}` : ""}</div>
                </div>
                <Button variant="outline" size="sm" onClick={()=>download(b.id, b.kind)} disabled={!online} data-testid={`backup-download-${b.id}`}><Download size={14}/></Button>
                <Button variant="outline" size="sm" onClick={()=>setRestoreTarget(b)} disabled={!online} data-testid={`backup-restore-${b.id}`}><History size={14}/></Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="backup-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Restaurar este backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a reemplazar TODOS los datos actuales (fincas, lotes, animales, eventos e
              inventarios) por los del backup del{" "}
              <strong>{restoreTarget ? formatDateTime(restoreTarget.snapshot_date) : ""}</strong>{" "}
              ({restoreTarget ? Object.values(restoreTarget.entities_count || {}).reduce((a, v) => a + v, 0) : 0} registros).
              Antes de restaurar se va a generar automáticamente un backup del estado actual, para
              poder deshacer esto si hace falta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-restore-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRestore} disabled={restoring} data-testid="backup-restore-confirm" className="bg-destructive hover:bg-destructive/90">
              {restoring ? "Restaurando…" : "Sí, restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!importPreview} onOpenChange={(o) => !o && cancelImport()}>
        <AlertDialogContent data-testid="migration-import-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Importar este archivo?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <div>
                  Exportado el <strong>{importPreview ? formatDateTime(importPreview.exported_at) : ""}</strong>
                  {importPreview?.source_owner_email ? <> desde la cuenta <strong>{importPreview.source_owner_email}</strong></> : null}.
                </div>
                <div className="text-xs grid grid-cols-2 gap-1" data-testid="migration-import-counts">
                  <span>Fincas: {importPreview?.counts?.farms || 0}</span>
                  <span>Lotes: {importPreview?.counts?.lots || 0}</span>
                  <span>Animales: {importPreview?.counts?.animals || 0}</span>
                  <span>Eventos: {importPreview?.counts?.events || 0}</span>
                  <span>Inventarios: {importPreview?.counts?.inventories || 0}</span>
                  <span>Total: {importPreview?.total || 0}</span>
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  <label className="flex items-center gap-2 text-xs" data-testid="migration-mode-replace">
                    <input type="radio" name="import-mode" checked={importMode === "replace"} onChange={() => setImportMode("replace")} />
                    Reemplazar — borra los datos actuales de esta cuenta y deja exactamente lo del archivo.
                  </label>
                  <label className="flex items-center gap-2 text-xs" data-testid="migration-mode-merge">
                    <input type="radio" name="import-mode" checked={importMode === "merge"} onChange={() => setImportMode("merge")} />
                    Combinar — agrega/actualiza sin borrar lo que ya había.
                  </label>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="migration-import-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmImport} disabled={importing} data-testid="migration-import-confirm" className="bg-destructive hover:bg-destructive/90">
              {importing ? "Importando…" : "Sí, importar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
